import React, { useEffect, useRef, useState } from "react";
import { BiSolidChevronDown } from "react-icons/bi";
import { Popover, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchLoans } from "../../store/Slices/LoanSlice";
import { getLoanBalance, getLoanTypeLabel } from "../../utils/LoanConverter";

const fmt = (n) => Number(n || 0).toLocaleString("en-PK");

const LoanPartySelector = ({
  selectedParty,
  setSelectedParty,
  placeholder = "Select existing party or type new name below",
  label = "Loan party account",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const Loans = useSelector((state) => state.Loans);

  useEffect(() => {
    dispatch(fetchLoans());
  }, [dispatch]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [open]);

  const parties = Array.isArray(Loans.data) ? Loans.data : [];

  const filtered = parties.filter((row) => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    return (
      String(row.partyName || "").toLowerCase().includes(q) ||
      String(row.phone || "").includes(q)
    );
  });

  return (
    <div className="flex flex-col gap-y-1 w-full">
      {label && <p className="text-sm font-bold text-main px-1">{label}</p>}
      <div
        className="relative text-left cursor-pointer transition-colors ease-in-out duration-200 border border-line hover:border-main bg-card text-ink px-4 py-3 rounded-lg font-quicksand font-bold text-sm flex gap-x-6 items-center active:scale-[.99]"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <div className="flex justify-between items-center gap-2 min-w-0">
          <span className="font-[600] truncate text-left">
            {selectedParty?._id
              ? `${selectedParty.partyName} · ${getLoanTypeLabel(selectedParty)} · Bal Rs ${fmt(getLoanBalance(selectedParty))}`
              : placeholder}
          </span>
          <BiSolidChevronDown className="text-xl shrink-0" />
        </div>
      </div>
      {selectedParty?._id && (
        <button
          type="button"
          className="text-xs text-left text-main font-semibold px-1 hover:underline"
          onClick={() => setSelectedParty("")}
        >
          Clear — add as new party instead
        </button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Typography sx={{ p: 2, backgroundColor: "#ffffff", overflow: "hidden", borderRadius: "12px" }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search party…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full mb-3 px-3 py-2 rounded-lg text-ink text-sm outline-none"
          />
          <div className="max-h-[240px] overflow-y-auto flex flex-col gap-2">
            {filtered.map((row) => (
              <div
                key={row._id}
                className="cursor-pointer rounded-lg bg-white/10 px-3 py-2 hover:bg-white/20"
                onClick={() => {
                  setSelectedParty(row);
                  setAnchorEl(null);
                }}
              >
                <p className="font-bold">{row.partyName}</p>
                <p className="text-xs opacity-90">
                  {getLoanTypeLabel(row)} · Outstanding Rs {fmt(getLoanBalance(row))}
                  {row.phone ? ` · ${row.phone}` : ""}
                </p>
              </div>
            ))}
            {!filtered.length && (
              <p className="text-sm opacity-80">No matching party — type name below</p>
            )}
          </div>
        </Typography>
      </Popover>
    </div>
  );
};

export default LoanPartySelector;
